<template>
  <div class="project-detail">
    <!-- Header -->
    <div class="project-detail__header">
      <button class="project-detail__back" @click="$emit('back')">
        ← Volver
      </button>
      <el-dropdown class="project-detail__menu" @command="handleHeaderCommand">
        <button class="project-detail__menu-btn">
          <i class="el-icon-more"></i>
        </button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="edit">
            <i class="el-icon-edit"></i> Editar proyecto
          </el-dropdown-item>
          <el-dropdown-item command="delete">
            <i class="el-icon-delete"></i> Eliminar proyecto
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      <div class="project-detail__meta">
        <div>
          <h2 class="project-detail__name">{{ detail.name }}</h2>
          <span class="project-detail__client" v-if="detail.client">{{
            detail.client.name
          }}</span>
        </div>
        <div
          class="project-detail__dates"
          v-if="detail.start_date || detail.end_date"
        >
          <div class="project-detail__date-block" v-if="detail.start_date">
            <span class="project-detail__date-label">Inicio</span>
            <span class="project-detail__date-value">{{
              formatDate(detail.start_date)
            }}</span>
          </div>
          <div class="project-detail__date-block" v-if="detail.end_date">
            <span class="project-detail__date-label">Entrega</span>
            <span class="project-detail__date-value">{{
              formatDate(detail.end_date)
            }}</span>
          </div>
          <div
            v-if="daysCounter"
            class="project-detail__date-block project-detail__date-block--counter"
          >
            <span class="project-detail__date-label">{{
              daysCounter.label
            }}</span>
            <span class="project-detail__date-value">{{
              daysCounter.value
            }}</span>
          </div>
        </div>
      </div>
      <div class="project-detail__kpis">
        <div class="kpi">
          <span class="kpi__label">Presupuesto</span>
          <span class="kpi__value"
            >${{ formatNum(detail.pl && detail.pl.budget) }}</span
          >
        </div>
        <div class="kpi">
          <span class="kpi__label">Ingresos</span>
          <span class="kpi__value"
            >${{ formatNum(detail.pl && detail.pl.total_invoiced) }}</span
          >
        </div>
        <div class="kpi">
          <span class="kpi__label">Gastos</span>
          <span class="kpi__value kpi__value--red"
            >${{ formatNum(detail.pl && detail.pl.total_expenses) }}</span
          >
        </div>
        <div class="kpi">
          <span class="kpi__label">Utilidad</span>
          <span class="kpi__value kpi__value--green"
            >${{ formatNum(detail.pl && detail.pl.gross_profit) }}</span
          >
        </div>
      </div>
    </div>

    <!-- Skeleton loading -->
    <template v-if="loading">
      <div class="pd-skeleton__header">
        <div class="pd-sk pd-sk--back"></div>
        <div class="pd-sk pd-sk--title"></div>
        <div class="pd-sk pd-sk--subtitle"></div>
        <div class="pd-skeleton__kpis">
          <div class="pd-sk pd-sk--kpi" v-for="i in 4" :key="i"></div>
        </div>
      </div>
      <div class="pd-skeleton__tabs">
        <div class="pd-sk pd-sk--tab" v-for="i in 3" :key="i"></div>
      </div>
      <div class="pd-skeleton__table">
        <div class="pd-sk pd-sk--row" v-for="i in 3" :key="i"></div>
      </div>
    </template>

    <!-- Sub-tabs -->
    <el-tabs v-else v-model="activeTab" class="project-detail__tabs">
      <!-- Facturas -->
      <el-tab-pane label="Facturas" name="invoices">
        <div
          class="inv-table-scroll"
          v-if="detail.invoices && detail.invoices.length > 0"
        >
          <div class="inv-table">
            <div class="inv-table__header">
              <span>Número</span>
              <span>Cliente</span>
              <span>Total</span>
              <span>Cobrado</span>
              <span>Saldo</span>
              <span>Cobro</span>
              <span></span>
            </div>
            <div
              class="inv-table__row"
              v-for="inv in detail.invoices"
              :key="inv.id"
            >
              <span
                class="inv-cell inv-cell--number inv-cell--link"
                @click="openPreview(inv)"
                >{{ inv.number }}</span
              >
              <span class="inv-cell">{{ inv.client || "—" }}</span>
              <span class="inv-cell">${{ formatNum(inv.total) }}</span>
              <span class="inv-cell">${{ formatNum(inv.total_paid) }}</span>
              <span
                class="inv-cell"
                :class="inv.balance > 0 ? 'text-red' : 'text-green'"
                >${{ formatNum(inv.balance) }}</span
              >
              <span class="inv-cell">
                <span
                  class="status-badge"
                  :class="`status-badge--${inv.payment_status}`"
                >
                  {{ paymentStatusLabel(inv.payment_status) }}
                </span>
              </span>
              <span class="inv-cell inv-cell--actions">
                <el-tooltip
                  content="Cobrar"
                  placement="top"
                  v-if="inv.balance > 0"
                >
                  <button
                    class="inv-action-btn inv-action-btn--primary"
                    @click="openPayment(inv)"
                  >
                    <i class="el-icon-coin"></i>
                  </button>
                </el-tooltip>
                <el-tooltip content="Desvincular" placement="top">
                  <button
                    class="inv-action-btn inv-action-btn--danger"
                    @click="detachInvoice(inv)"
                  >
                    <i class="el-icon-delete"></i>
                  </button>
                </el-tooltip>
              </span>
            </div>
          </div>
        </div>
        <div v-else-if="!loading" class="inv-empty">
          No hay facturas vinculadas aún.
        </div>
        <LinkPlusComponent
          title="Vincular factura"
          @onClick="showLinkInvoice = true"
          style="margin-top: 16px; display: block;"
        />
      </el-tab-pane>

      <!-- Gastos -->
      <el-tab-pane label="Gastos" name="expenses">
        <div
          class="inv-table-scroll"
          v-if="detail.expenses && detail.expenses.length > 0"
        >
          <div class="inv-table inv-table--expenses">
            <div class="inv-table__header">
              <span>Fecha</span>
              <span>Descripción</span>
              <span>Categoría</span>
              <span>Monto</span>
              <span>Proveedor</span>
              <span></span>
            </div>
            <div
              class="inv-table__row"
              v-for="exp in detail.expenses"
              :key="exp.id"
            >
              <span class="inv-cell">{{ formatDate(exp.expense_date) }}</span>
              <span class="inv-cell">{{ exp.description || "—" }}</span>
              <span class="inv-cell">{{ exp.category || "—" }}</span>
              <span class="inv-cell">${{ formatNum(exp.amount) }}</span>
              <span class="inv-cell">{{ exp.vendor || "—" }}</span>
              <span class="inv-cell inv-cell--actions">
                <el-tooltip content="Eliminar" placement="top">
                  <button
                    class="inv-action-btn inv-action-btn--danger"
                    @click="deleteExpense(exp)"
                  >
                    <i class="el-icon-delete"></i>
                  </button>
                </el-tooltip>
              </span>
            </div>
          </div>
        </div>
        <div v-else-if="!loading" class="inv-empty">
          No hay gastos registrados aún.
        </div>
        <LinkPlusComponent
          title="Agregar gasto"
          @onClick="showExpenseForm = true"
          style="margin-top: 16px; display: block;"
        />
      </el-tab-pane>

      <!-- Resumen P&L -->
      <el-tab-pane label="Resumen P&L" name="pl">
        <div class="pl-summary" v-if="detail.pl">
          <div class="pl-row pl-row--income">
            <span>Ingresos facturados (sin IVA)</span>
            <span>${{ formatNum(detail.pl.total_invoiced) }}</span>
          </div>
          <div class="pl-row pl-row--expense">
            <span>Gastos directos</span>
            <span>- ${{ formatNum(detail.pl.total_expenses) }}</span>
          </div>
          <div class="pl-row pl-row--profit">
            <span><strong>Utilidad bruta</strong></span>
            <span
              ><strong>${{ formatNum(detail.pl.gross_profit) }}</strong></span
            >
          </div>
          <div class="pl-row">
            <span>Margen %</span>
            <span>{{ detail.pl.margin_pct }}%</span>
          </div>
          <div class="pl-divider"></div>
          <div class="pl-row" v-if="detail.pl.budget">
            <span>Presupuesto total</span>
            <span>${{ formatNum(detail.pl.budget) }}</span>
          </div>
          <div class="pl-row" v-if="detail.pl.remaining_to_invoice !== null">
            <span>Restante por facturar</span>
            <span :class="detail.pl.remaining_to_invoice < 0 ? 'text-red' : ''">
              ${{ formatNum(detail.pl.remaining_to_invoice) }}
            </span>
          </div>
          <div class="pl-divider"></div>
          <div class="pl-row">
            <span>Cobrado</span>
            <span class="text-green"
              >${{ formatNum(detail.pl.collected) }}</span
            >
          </div>
          <div class="pl-row">
            <span>Por cobrar</span>
            <span
              :class="
                detail.pl.pending_collection > 0 ? 'text-red' : 'text-green'
              "
            >
              ${{ formatNum(detail.pl.pending_collection) }}
            </span>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Payment modal -->
    <PaymentModal
      v-if="paymentInvoice"
      :invoice="paymentInvoice"
      @close="paymentInvoice = null"
      @payment-registered="onPaymentDone"
    />

    <!-- Link invoice modal -->
    <LinkInvoiceModal
      v-if="showLinkInvoice"
      :project-id="projectId"
      :project-name="detail.name"
      :linked-ids="linkedInvoiceIds"
      @close="showLinkInvoice = false"
      @linked="onInvoiceLinked"
    />

    <!-- Expense form modal (from expenses module) -->
    <ExpenseFormModal
      :show="showExpenseForm"
      :default-project-id="projectId"
      @close="showExpenseForm = false"
      @saved="onExpenseAdded"
    />

    <!-- Edit project modal -->
    <ProjectFormModal
      v-if="showEditProject"
      :project="detail"
      @close="showEditProject = false"
      @updated="onProjectUpdated"
    />

    <!-- Invoice preview -->
    <InvoicePreview
      v-if="previewInvoice"
      :invoice="previewInvoice"
      :showModal="showPreview"
      :status="previewInvoice.status"
      @close="closePreview"
      @action="() => {}"
    />
  </div>
</template>

<script>
import projectService from "../services/projectService";
import PaymentModal from "./PaymentModal";
import LinkInvoiceModal from "./LinkInvoiceModal";
import ExpenseFormModal from "../../expenses/components/ExpenseFormModal";
import ProjectFormModal from "./ProjectFormModal";
import LinkPlusComponent from "../../../../components/LinkPlus";
import InvoicePreview from "../../invoices/components/InvoicePreview";
import Api from "@/config/Api";

export default {
  name: "ProjectDetail",
  components: {
    PaymentModal,
    LinkInvoiceModal,
    ExpenseFormModal,
    ProjectFormModal,
    LinkPlusComponent,
    InvoicePreview
  },
  props: {
    projectId: { type: Number, required: true }
  },
  data() {
    return {
      detail: {},
      loading: false,
      activeTab: "invoices",
      paymentInvoice: null,
      showLinkInvoice: false,
      showExpenseForm: false,
      showEditProject: false,
      previewInvoice: null,
      showPreview: false
    };
  },
  computed: {
    linkedInvoiceIds() {
      return (this.detail.invoices || []).map(i => i.id);
    },
    daysCounter() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const DAY = 86400000;

      const parseDate = val => {
        if (!val) return null;
        const d = new Date(val);
        if (isNaN(d.getTime())) return null;
        d.setHours(0, 0, 0, 0);
        return d;
      };

      const pluralDias = n =>
        Math.abs(n) + " día" + (Math.abs(n) !== 1 ? "s" : "");

      const start = parseDate(this.detail.start_date);
      const end = parseDate(this.detail.end_date);

      if (!start && !end) return null;

      if (start && start > today) {
        return {
          label: "Faltan para iniciar",
          value: pluralDias(Math.ceil((start - today) / DAY))
        };
      }

      if (start) {
        const elapsed = Math.floor((today - start) / DAY);
        if (end) {
          const total = Math.ceil((end - start) / DAY);
          const remaining = Math.ceil((end - today) / DAY);
          if (remaining < 0) {
            return { label: "Días de retraso", value: pluralDias(remaining) };
          }
          return {
            label: "Transcurrido",
            value: elapsed + " de " + total + " días"
          };
        }
        return { label: "Días transcurridos", value: pluralDias(elapsed) };
      }

      if (end) {
        const remaining = Math.ceil((end - today) / DAY);
        if (remaining < 0) {
          return { label: "Días de retraso", value: pluralDias(remaining) };
        }
        return { label: "Días restantes", value: pluralDias(remaining) };
      }

      return null;
    }
  },
  mounted() {
    this.loadDetail();
  },
  methods: {
    async loadDetail() {
      this.loading = true;
      try {
        const res = await projectService.getProject(this.projectId);
        if (res.data.success) this.detail = res.data.data;
      } finally {
        this.loading = false;
      }
    },
    openPayment(invoice) {
      this.paymentInvoice = invoice;
    },
    onPaymentDone() {
      this.paymentInvoice = null;
      this.loadDetail(); // Reload full project to refresh KPIs + invoice balances
      this.$emit("payment-registered");
    },
    onInvoiceLinked() {
      this.showLinkInvoice = false;
      this.loadDetail();
    },
    onExpenseAdded() {
      this.showExpenseForm = false;
      this.loadDetail();
    },
    handleHeaderCommand(cmd) {
      if (cmd === "edit") {
        this.showEditProject = true;
      } else if (cmd === "delete") {
        this.confirmDeleteProject();
      }
    },
    async confirmDeleteProject() {
      try {
        await this.$confirm(
          "¿Estás seguro de eliminar este proyecto? Esta acción no se puede deshacer.",
          "Eliminar proyecto",
          {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );
        await projectService.deleteProject(this.projectId);
        this.$notifications.success({ message: "Proyecto eliminado." });
        this.$emit("back");
      } catch (e) {
        // Cancelled
      }
    },
    onProjectUpdated() {
      this.showEditProject = false;
      this.loadDetail();
    },
    async detachInvoice(invoice) {
      try {
        await this.$confirm(
          `¿Desvincular la factura ${invoice.number} de este proyecto?`,
          "Desvincular factura",
          {
            confirmButtonText: "Desvincular",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );
        await projectService.detachInvoice(this.projectId, invoice.id);
        this.$notifications.success({ message: "Factura desvinculada." });
        this.loadDetail();
      } catch (e) {
        // cancelled
      }
    },
    async deleteExpense(expense) {
      try {
        await this.$confirm(
          `¿Eliminar "${expense.description ||
            "este gasto"}"? Esta acción no se puede deshacer.`,
          "Eliminar gasto",
          {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );
        await projectService.deleteExpense(expense.id);
        this.$notifications.success({ message: "Gasto eliminado." });
        this.loadDetail();
      } catch (e) {
        /* cancelled */
      }
    },
    async openPreview(invoice) {
      try {
        const res = await Api().get(`/invoices/${invoice.id}`);
        this.previewInvoice = res.data.data || res.data;
      } catch (e) {
        this.previewInvoice = invoice;
      }
      this.showPreview = true;
    },
    closePreview() {
      this.showPreview = false;
      this.previewInvoice = null;
    },
    paymentStatusLabel(s) {
      const m = { paid: "Pagado", partial: "Parcial", pending: "Pendiente" };
      return m[s] || s;
    },
    formatNum(val) {
      if (!val && val !== 0) return "—";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.project-detail {
  &__header {
    position: relative;
    background: linear-gradient(135deg, #2d2b55, #0e8c87, #3fdbbf);
    border-radius: 14px;
    padding: 24px 28px;
    margin-bottom: 24px;
    color: #fff;
  }

  &__back {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 8px;
    cursor: pointer;
    margin-bottom: 16px;
    transition: background 0.2s;
    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }

  &__menu {
    position: absolute;
    top: 14px;
    right: 14px;
  }

  &__menu-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
  }

  &__name {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 4px;
  }

  &__client {
    font-size: 13px;
    opacity: 0.75;
  }

  &__dates {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  &__date-block {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 6px 14px;
    text-align: center;

    &--counter {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &__date-label {
    display: block;
    font-size: 10px;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 2px;
  }

  &__date-value {
    display: block;
    font-size: 13px;
    font-weight: 600;
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  &__loading {
    text-align: center;
    padding: 60px;
    color: #aaa;
  }

  &__tabs {
    margin-top: 8px;
  }
}

.kpi {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 12px 16px;

  &__label {
    display: block;
    font-size: 11px;
    opacity: 0.85;
    margin-bottom: 4px;
  }
  &__value {
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    &--green {
      color: #d4ffe8;
    }
    &--red {
      color: #ffd4d4;
    }
  }
}

.status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;

  &--paid {
    background: #e8faf6;
    color: #1d9e75;
  }
  &--partial {
    background: #fff8e6;
    color: #d08b00;
  }
  &--pending {
    background: #f0f0f8;
    color: #888;
  }
}

.pl-summary {
  max-width: 100%;
  background: #fff;
  border: 1.5px solid #eef0f8;
  border-radius: 12px;
  padding: 20px 24px;
}

.pl-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: #2d2b55;

  &--income span:last-child {
    color: #1d9e75;
    font-weight: 600;
  }
  &--expense span:last-child {
    color: #ef4444;
    font-weight: 600;
  }
  &--profit {
    font-size: 15px;
    border-top: 1.5px solid #eef0f8;
    margin-top: 4px;
    padding-top: 12px;
  }
}

.pl-divider {
  border-top: 1px dashed #eef0f8;
  margin: 8px 0;
}

.text-green {
  color: #1d9e75;
  font-weight: 600;
}
.text-red {
  color: #ef4444;
  font-weight: 600;
}

/* ── Invoices Custom Table ── */
.inv-table {
  border: 1px solid #eef0f8;
  border-radius: 10px;
  overflow: hidden;

  &__header,
  &__row {
    display: grid;
    grid-template-columns: 160px 1fr 110px 110px 110px 110px 76px;
    align-items: center;
  }

  &--expenses &__header,
  &--expenses &__row {
    grid-template-columns: 110px 1fr 130px 110px 130px 60px;
  }

  &__header {
    background: #f7f8fa;
    border-bottom: 1px solid #eef0f8;

    span {
      padding: 9px 12px;
      font-size: 11px;
      font-weight: 600;
      color: #9a9ab0;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
  }

  &__row {
    border-bottom: 1px solid #f0f0f5;
    transition: background 0.12s;

    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background: #f9f9fc;
    }
  }
}

.inv-cell {
  padding: 10px 12px;
  font-size: 13px;
  color: #2d2b55;

  &--number {
    font-weight: 600;
    font-size: 12px;
  }

  &--link {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: all 0.15s;
    &:hover {
      color: #3fdbbf;
      text-decoration-color: #3fdbbf;
    }
  }

  &--actions {
    display: flex;
    gap: 4px;
    justify-content: flex-end;
    padding-right: 10px;
  }
}

.inv-action-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: all 0.15s;
  background: transparent;

  &--primary {
    border: 1.5px solid #3fdbbf;
    color: #3fdbbf;
    &:hover {
      background: #e8faf6;
      border-color: #0e8c87;
      color: #0e8c87;
    }
  }

  &--danger {
    border: 1.5px solid #dde0ea;
    color: #b0b4c0;
    &:hover {
      background: #fef0f0;
      border-color: #ef4444;
      color: #ef4444;
    }
  }
}

.inv-empty {
  padding: 28px;
  text-align: center;
  font-size: 13px;
  color: #c0c4cc;
}

/* ── Detail Skeleton ── */
@keyframes pd-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.pd-sk {
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.25) 25%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0.25) 75%
  );
  background-size: 200% 100%;
  animation: pd-shimmer 1.3s ease-in-out infinite;

  &--back {
    width: 80px;
    height: 30px;
    border-radius: 8px;
    margin-bottom: 16px;
  }
  &--title {
    width: 260px;
    height: 26px;
    margin-bottom: 8px;
  }
  &--subtitle {
    width: 120px;
    height: 14px;
    margin-bottom: 20px;
  }
  &--kpi {
    height: 64px;
    border-radius: 10px;
  }
  &--tab {
    width: 70px;
    height: 14px;
    border-radius: 6px;
  }
  &--row {
    width: 100%;
    height: 44px;
    border-radius: 8px;
  }
}

.pd-skeleton {
  &__header {
    background: linear-gradient(135deg, #2d2b55, #0e8c87, #3fdbbf);
    border-radius: 14px;
    padding: 24px 28px;
    margin-bottom: 24px;
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  &__tabs {
    display: flex;
    gap: 24px;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eef0f8;
  }

  &__table {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

/* ── Scroll wrapper for tables ── */
.inv-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ── Mobile ── */
@media (max-width: 768px) {
  .project-detail {
    &__header {
      padding: 16px;
    }

    &__meta {
      flex-direction: column;
      gap: 12px;
    }

    &__dates {
      flex-wrap: wrap;
      gap: 8px;
    }

    &__date-block {
      padding: 6px 10px;
    }

    &__name {
      font-size: 16px;
    }

    &__kpis {
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
  }

  .kpi {
    padding: 10px 12px;

    &__value {
      font-size: 15px;
    }
  }

  .inv-table {
    &__header,
    &__row {
      min-width: 680px;
    }

    &--expenses &__header,
    &--expenses &__row {
      min-width: 560px;
    }
  }

  .pd-skeleton__kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .pd-skeleton__header {
    padding: 16px;
  }
}
</style>
